import { useRef, useState, useEffect } from 'preact/hooks';
import { useFetchDevices } from './useFetchDevices';
import { sendRequest } from '../../../tools';
import { ConfirmSend } from './ConfirmSend';
import Device from './components/Device';
import FooterAfterSubmit from './components/FooterAfterSubmit';
import { containerStyle, modalStyle, alertStyle } from './components/Styles';

export default function MeterReadings({ account, contract, onClose, enterUrl }) {
  const { devices, unit, loading, error, refetch } = useFetchDevices(enterUrl);

  const saldo = useRef(0);
  const [calculationProgress, setCalculationProgress] = useState(false);
  const [counterValues, setCounterValues] = useState({});
  const [consumptionValues, setConsumptionValues] = useState({});
  const [calcPayment, setCalcPayment] = useState({});
  const [calcResultValues, setCalcResultValues] = useState({});
  const [calcErrors, setCalcErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [totalSumValue, setTotalSumValue] = useState(0);

  var infoModalTrigger = true;
  var isHeating = useRef(false);

  // Initialize calcErrors for all devices when devices are loaded
  useEffect(() => {
    if (devices && devices.length > 0) {
      const initialErrors = {};
      devices.forEach(device => {
        initialErrors[device.number] = true;
        //допущение - только 1ПУ на договоре по отоплению
        if (device.isHeating) {
          isHeating.current = true;
        }
      });
      setCalcErrors(initialErrors);
    }
  }, [devices]);

  // Properly manage event listeners with useEffect
  useEffect(() => {
    document.addEventListener('keydown', keyDownEscape);
    return () => {
      document.removeEventListener('keydown', keyDownEscape);
    };
  }, []);

  // Recalculate total sum when calcPayment changes
  useEffect(() => {
    setTotalSumValue(saldo.current + calcPaymentTotal());
  }, [calcPayment]);

  // Handlers
  const calcPaymentTotal = () => {
    let total = 0;
    Object.values(calcPayment).forEach(value => {
      total += value;
    });
    return total;
  };

  function hasCalcErrors() {
    return Object.values(calcErrors).some(error => error);
  }

  function formatError(errorText) {
    if (errorText.includes('превышает максимальное значение суточного потребления')) {
      errorText = 'Слишком большой расход по точке учета. Скорректируйте введенные показания.';
    };
    return `
    <div class="testimony-modal__field-indicators-item" data-result="error" style={{ marginBottom: '10px' }}>
      <div class="testimony-modal__input-error">
        <div class="testimony-modal__input-error-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M2 12C2 6.48 6.48 2 12 2C17.52 2 22 6.48 22 12C22 17.52 17.52 22 12 22C6.48 22 2 17.52 2 12ZM15.9929 7L17 8.00714L13.0071 12L17 15.9929L15.9929 17L12 13.0071L8.00714 17L7 15.9929L10.9929 12L7 8.00714L8.00714 7L12 10.9929L15.9929 7Z" fill="black"/>
          </svg>
        </div>
        <div class="testimony-modal__input-error-text" data-result-text style="width: auto;">
            ${errorText}
        </div>
      </div>
    </div>`;
  }

  // Handler for meter reading input
  function handleMeterReadingInput(device, e) {
    const value = e.target.value;
    setCounterValues(prev => ({ ...prev, [device.number]: value }));

    // Calculate and update consumption
    const currentValue = parseFloat(value);
    const previousValue = parseFloat(device.meterReading);

    let consumption = '-';
    if (!isNaN(currentValue) && !isNaN(previousValue) && currentValue > previousValue) {
      const diff = currentValue - previousValue;
      consumption = Number.isInteger(diff) ? diff.toString() : diff.toFixed(5);
    }
    setConsumptionValues(prev => ({ ...prev, [device.number]: consumption }));
    if (!calculationProgress) {
      calculate(device.number, currentValue, previousValue, consumption);
    }
  }

  // Save meter readings
  const save = async () => {
    const formData = new FormData();
    formData.append('remember', 1);
    devices.forEach(device => {
      formData.append('EnterMeterReadings[readings][' + device.number + ']', counterValues[device.number]);
    });

    try {
      const response = await sendRequest(enterUrl, { body: formData, type: 'POST', showLoader: true });
      if (response.status === 200) {
        setSubmitted(true);
      } else {
        throw new Error(`Status ${response.status}. Status text: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Error fetching:', err);
    } finally {
    }
  }

  // Calculate payment
  const calculate = async (deviceNumber, currentValue, previousValue, consumption) => {
    const preErrMessage = (currentValue >= previousValue)
      ? ''
      : formatError('Текущие показания не могут быть меньше предыдущих.');

    setCalcResultValues(prev => ({ ...prev, [deviceNumber]: preErrMessage }));
    setCalcPayment(prev => ({ ...prev, [deviceNumber]: 0 }));

    if (currentValue === previousValue) {
      setCalcErrors(prev => ({ ...prev, [deviceNumber]: false }));
      return;
    }
    if (currentValue < previousValue) {
      setCalcErrors(prev => ({ ...prev, [deviceNumber]: true }));
      return;
    }

    setCalcErrors(prev => ({ ...prev, [deviceNumber]: true }));
    if (consumption === '-') {
      return;
    }

    setCalculationProgress(true);

    const formData = new FormData();
    formData.append('calculate', 1);
    formData.append('EnterMeterReadings[readings][' + deviceNumber + ']', currentValue);

    try {
      const response = await sendRequest(enterUrl, { body: formData, type: 'POST', showLoader: true });
      if (response.status === 200) {
        const data = response.data;

        setCalcErrors(prev => ({ ...prev, [deviceNumber]: data.Error }));
        if (data.Error) {
          setCalcResultValues(prev => ({ ...prev, [deviceNumber]: formatError(data.ErrorText) }));
          return;
        }
        if (data.OutValue.length == 0) {
          setCalcResultValues(prev => ({ ...prev, [deviceNumber]: '' }));
          return;
        }

        const services = data.OutValue[0].Services;
        let html = '';
        services.forEach((item) => {
          html += makeMessage(item);
        });
        setCalcResultValues(prev => ({ ...prev, [deviceNumber]: html }));
        setCalcPayment(prev => ({ ...prev, [deviceNumber]: data.Sum }));

        // Начальное сальдо по расчетам
        if (saldo.current == 0) {
          saldo.current = data.NachSum - data.Sum;
        }

      } else {
        throw new Error(`Status ${response.status}. Status text: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Error fetching:', err);
    } finally {
      // Clear calculating state
      setCalculationProgress(false);
    }
  };

  function makeMessage(item) {
    let name = item.Name;
    name = name.charAt(0).toUpperCase() + name.slice(1);
    return '<strong>' + name + '</strong><br/>' +
      'Тариф ' + item.Tarif +
      ' Расход ' + item.Value +
      ' = Сумма ' + item.SumValue + ' руб.<br/>';
  }

  function keyDownEscape(event) {
    if (event.key === 'Escape') {
      const isNotCombinedKey = !(event.ctrlKey || event.altKey || event.shiftKey);
      if (isNotCombinedKey) {
        //onClose();
      }
    }
  }

  function handleCloseFansyBox() {
    window.parent.jQuery.fancybox.close();
  }

  // Delete prev value
  async function handleDeletePrevValue() {
    handleCloseFansyBox();

    const formData = new FormData();
    formData.append('contractId', contract.id);
    try {
      const url = '/debts/delete-value/';
      const response = await sendRequest(url, { body: formData, type: 'POST', showLoader: true });
      if (response.status === 200) {
        const data = response.data;
      } else {
        throw new Error(`Status ${response.status}. Status text: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Error fetching:', err);
    } finally {
      refetch();
    }
  }

  return (
    <div style={containerStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
          <p class="testimony-modal__text" style={alertStyle}>
            Передача показаний необходима для точного расчета суммы задолженности
          </p>
          <button style={{ height: 'fit-content' }} onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="black"></path>
            </svg>
          </button>
        </div>
        <div>
          <div>
            <div class="testimony-modal__address">{account.address}</div>
            <div class="testimony-modal__account">ЛС {account.id}</div>
          </div>

          {error && (
            <div class="error-message">
              Ошибка загрузки данных: {error}
            </div>
          )}

          {loading ? (
            <div>Загрузка...</div>
          ) : (
            devices && devices.map((device) => (
              <Device
                devices={devices}
                device={device}
                unit={unit}
                counterValues={counterValues}
                handleMeterReadingInput={handleMeterReadingInput}
                calculationProgress={calculationProgress}
                calcResultValues={calcResultValues}
                consumptionValues={consumptionValues}
                calcPayment={calcPayment}
                handleDeletePrevValue={handleDeletePrevValue}
                handleCloseFansyBox={handleCloseFansyBox}
              />
            ))
          )}

          {!loading && calcPaymentTotal() > 0 && (
            <div class="testimony-modal__field-indicators-item" data-result="success">
              <div class="testimony-modal__price-info">
                <div class="testimony-modal__price-title">Итого к оплате с учетом поступивших оплат и перерасчетов</div>
                <div class="testimony-modal__price-number">{totalSumValue.toFixed(2)} *</div>
                <div class="testimony-modal__price-title" style={{ color: '#1a47a9' }}>* {advText(totalSumValue)}</div>
              </div>
            </div>
          )}

          {!submitted ? (
            <div class="modal-1__section">
              <button
                class="button-secondary modal-1__button"
                onClick={handleSubmitMeterReading}
                disabled={isSubmitDisabled()}
              >
                Передать показания
              </button>
            </div>
          ) : (
            <FooterAfterSubmit
              contractId={contract.id}
              accountId={account.id}
              onClose={onClose}
              infoModalTrigger={infoModalTrigger}
            />
          )}
        </div>
      </div>
    </div>
  );

  // Function to check if submit button should be disabled
  function isSubmitDisabled() {
    return Object.keys(counterValues).length === 0 || hasCalcErrors() || submitted;
  }

  function advText(sum) {
    if (sum === 0) {
      return 'Задолженности нет';
    } else if (sum > 0) {
      return 'Долг';
    } else {
      return 'Переплата';
    }
  }

  function handleSubmitMeterReading() {
    console.log('Submitting meter readings:', counterValues);

    let readings = [];
    devices.forEach(device => {
      readings.push({
        consumption: consumptionValues[device.number],
        cost: calcPayment[device.number],
        unit: unit
      });
    });

    ConfirmSend(readings, isHeating.current).then(function (result) {
      if (result) {
        save();
        infoModalTrigger = true;
        $(document).on('click', '.button-primary[data-reach-goal="avans_oplata2"]', function () {
          infoModalTrigger = false;
        });
        $(document).on('click', '.attention-modal__button span', function () {
          $.fancybox.close();
        });
      }
    })
  }
}
